import { useTranslation } from "react-i18next";
import { VonEmployeeTypesText } from "../../../../Constants/VON/EmployeeForm/vonEmployeeTypes";
import { filterServiceRolesForEmployeeType, isLicenseRequiredForEmployee } from "../../../../Utils/vonEmployeeUtils";
import { Dropdown } from "../../../FormComponents/Dropdown";
import helpers from "../../../../Utils/helpers";
import Tooltip, { TooltipContent, TooltipTrigger } from "../../../Overlays/Tooltip";
import SkynetIcon from "../../../Fundamentals/SkynetIcon";
import SortableTableHeader from "../../../Fundamentals/Tables/SortableTableHeader";

export const VonEditEmployeeLicensesForm = ({ form, onChange, service, vonAccounts, vonEmployeeTypeRestrictions, sortKey, sortOrder, handleSortChange }) => {
    const { data } = form;
    const { t } = useTranslation(); 

    const availableUserLicenses = service.licenses;
    const userLicensesInUse = data.employees.filter(x => {
        const account = vonAccounts.find(a => a.id === x.employeeId);
        return account && x.roleId && isLicenseRequiredForEmployee(account, vonEmployeeTypeRestrictions);
    }).length;
    const isAtLicenseCapacity = userLicensesInUse >= availableUserLicenses;

    const onChangeAccessRight = (roleId, vonAccount) => {
        const employee = data.employees.find(x => x.employeeId === vonAccount.id);

        const updatedEmployees = employee
            ? data.employees.map(x => x.employeeId === employee.employeeId ? { ...x, roleId: roleId == 0 ? null : roleId } : x)
            : [...data.employees, { employeeId: vonAccount.id, roleId: roleId == 0 ? null : roleId }];

        onChange(form.id, updatedEmployees, 'employees');
    }

    return (
        <div>
            <div className="table-responsive">
                <table className="table table-stripe table-hover">
                    <thead>
                        <tr>
                            <SortableTableHeader 
                                sortColumn="name"
                                currentSortKey={sortKey}
                                sortOrder={sortOrder}
                                onSort={handleSortChange}
                                showInactiveIcon={true}
                            >
                                {t("common.name")}
                            </SortableTableHeader>
                            <SortableTableHeader 
                                sortColumn="email"
                                currentSortKey={sortKey}
                                sortOrder={sortOrder}
                                onSort={handleSortChange}
                                showInactiveIcon={true}
                            >
                                {t("common.email")}
                            </SortableTableHeader>
                            <SortableTableHeader 
                                sortColumn="employeeType"
                                currentSortKey={sortKey}
                                sortOrder={sortOrder}
                                onSort={handleSortChange}
                                showInactiveIcon={true}
                            >
                                {t("employeeLicenses.userType")}
                            </SortableTableHeader>
                            <SortableTableHeader>
                                {t("vonaccountscontainer.expiryDate")}
                            </SortableTableHeader>
                            <SortableTableHeader>
                                {t("employeeLicenses.permission")}
                            </SortableTableHeader>
                        </tr>
                    </thead>
                    <tbody>
                        {vonAccounts.map(account =>
                            <VonAccountRow
                                key={account.id}
                                vonAccount={account}
                                data={data.employees.find(a => a.employeeId === account.id)}
                                isAtLicenseCapacity={isAtLicenseCapacity}
                                onChange={onChangeAccessRight}
                                vonEmployeeTypeRestrictions={vonEmployeeTypeRestrictions}
                                service={service} />)}
                    </tbody>
                </table>
            </div>
            <div>
                <p className="float-right">
                    {t("employeeLicenses.licensesInUse", { userLicensesInUse, availableUserLicenses })}
                </p>
            </div>
        </div>
    );
}

const VonAccountRow = ({ vonAccount, data, service, isAtLicenseCapacity, onChange, vonEmployeeTypeRestrictions }) => {
    const { t } = useTranslation();
    const willConsumeLicense = isLicenseRequiredForEmployee(vonAccount, vonEmployeeTypeRestrictions);
    const options = filterServiceRolesForEmployeeType(vonAccount.employeeType, service.vonServiceRoles, vonEmployeeTypeRestrictions);
    const isDisabled = options.length <= 1 || (willConsumeLicense && isAtLicenseCapacity && !data?.roleId);
    const expiryDate = vonAccount.roles?.find(r => r.applicationId === service.productNumber)?.expiryDate
    
    return <tr key={vonAccount.id}>
        <td className="d-flex align-items-center">
            {vonAccount.consultantId && (
                <Tooltip>
                    <TooltipTrigger>
                        <SkynetIcon 
                            icon="vismaicon-user-connection vismaicon-sm mr-8" 
                            title={t("vonaccountscontainer.consultant")}
                            aria-label={t("vonaccountscontainer.consultant")}
                        />
                    </TooltipTrigger>
                    <TooltipContent>
                        <div>{t("vonaccountscontainer.consultant")}</div>
                    </TooltipContent>
                </Tooltip>
            )}
            <span className="ml-8">{vonAccount.name}</span>
        </td>
        <td>{vonAccount.email}</td>
        <td>{t(VonEmployeeTypesText[vonAccount.employeeType])}</td>
        <td>
            {expiryDate ? helpers.formatDateAndTime(expiryDate) : ''}
        </td>
        <td className="col-3">
            <Dropdown
                options={options}
                property="displayName"
                value={data?.roleId || 0}
                onChange={(value) => onChange(value, vonAccount)}
                disabled={isDisabled}
            />
        </td>
    </tr>
};